import type { Task, WorkPhase } from "@myapp/contract";
import { deriveWorkPhase } from "@myapp/contract";
import {
  getTaskStatusColor,
  getTaskStatusLabel,
  getTaskTypeLabel,
  isDeadlineOverdue,
  useRevertTaskPhase,
  useStartTask,
  useStartTaskTravel,
  useTaskDetail,
} from "../../../entities/task";
import { useAuthStore } from "../../../features/auth";
import { getPrimaryWorkAction, usePhaseTaps } from "../../../features/work-session";
import type { PhaseTaps, PrimaryWorkAction } from "../../../features/work-session";
import { isSameDay } from "../../../shared/helpers/date";
import { formatDate, formatDateTime } from "../lib";

export interface WorkerTaskVm {
  task: Task;
  statusLabel: string;
  statusColor: string;
  typeLabel: string;
  phase: WorkPhase | null;
  primaryAction: PrimaryWorkAction | null;
  showActionButtons: boolean;
  /** "Im Kalender planen" / "Umdisponieren" is offered — independent of `primaryAction`, since an
   *  unscheduled task has no primary action at all but still needs a way to get one. */
  canPlan: boolean;
  isTimeScheduled: boolean;
  isToday: boolean;
  elapsedSince: string | null;
  terminText: string;
  /** True for "Nicht geplant" — muted, not the normal dark value colour. */
  terminUnscheduled: boolean;
  deadlineText: string;
  deadlineOverdue: boolean;
  technicianId: number;
}

export interface UseWorkerTaskResult {
  isLoading: boolean;
  isError: boolean;
  /** For the page's own 403/404 + ownership redirect — narrower than `vm`, which needs `data`. */
  data: Task | undefined;
  error: unknown;
  taps: PhaseTaps;
  /** null while loading or on error — narrow on this, not on isLoading/isError, before reading it. */
  vm: WorkerTaskVm | null;
}

/**
 * Data + derived view-model for the worker task screen — the direct counterpart of
 * `useWorkerAppointment`. `taps` is created unconditionally — the caller's hooks (and this one's)
 * must all run above any early return, since `data` is still undefined on the first renders.
 */
export function useWorkerTask(taskId: number | undefined): UseWorkerTaskResult {
  const { user } = useAuthStore();
  const { data, isLoading, isError, error } = useTaskDetail(taskId);

  const startTravel = useStartTaskTravel();
  const startWork = useStartTask();
  const revertPhase = useRevertTaskPhase();
  const taps = usePhaseTaps({
    'start-travel': () => startTravel.mutateAsync({ id: taskId! }),
    start: () => startWork.mutateAsync({ id: taskId! }),
    undo: () => revertPhase.mutateAsync({ id: taskId! }),
  });

  if (!data) {
    return { isLoading, isError, data, error, taps, vm: null };
  }

  // `plannedWorkStartsAt` at UTC midnight is how a date-only reservation is spelled — it still has
  // to be given a real time on the calendar before "Umdisponieren" (vs. "Im Kalender planen") and
  // the work-session flow make sense.
  const isTimeScheduled =
    !!data.schedule?.plannedWorkStartsAt && new Date(data.schedule.plannedWorkStartsAt).getUTCHours() !== 0;
  const phase = data.schedule ? deriveWorkPhase(data.schedule) : null;
  const isToday = isSameDay(data.schedule?.plannedWorkStartsAt, new Date());
  const isCompleted = data.status === "COMPLETED";

  const primaryAction = getPrimaryWorkAction({
    isCompleted,
    isScheduled: isTimeScheduled,
    isToday,
    phase,
    plannedTravelMinutes: data.schedule?.plannedTravelMinutes ?? 0,
  });

  // "Umdisponieren" only before the session opens; an unscheduled task keeps "Im Kalender planen"
  // regardless of day, since it has no day to compare against yet.
  const canPlan = phase === null && !isCompleted && (!isTimeScheduled || isToday);

  /** ISO anchor of the running phase — feeds the elapsed chip on the primary button. */
  const elapsedSince =
    phase === 'ANFAHRT'
      ? data.schedule?.travelStartedAt ?? null
      : phase === 'ARBEIT'
        ? data.schedule?.actualWorkStartsAt ?? null
        : phase === 'ABFAHRT'
          ? data.schedule?.actualEndsAt ?? null
          : null;

  const vm: WorkerTaskVm = {
    task: data,
    statusLabel: getTaskStatusLabel(data.status, data),
    statusColor: getTaskStatusColor(data.status, data),
    typeLabel: getTaskTypeLabel(data.taskType),
    phase,
    primaryAction,
    showActionButtons: primaryAction !== null || canPlan,
    canPlan,
    isTimeScheduled,
    isToday,
    elapsedSince,
    // Unscheduled reads as "Nicht geplant", not the generic "—" empty-value placeholder — the
    // task has no reservation at all, which is a different fact from "not filled in".
    terminText: data.schedule ? formatDateTime(data.schedule.plannedWorkStartsAt) : "Nicht geplant",
    terminUnscheduled: !data.schedule,
    deadlineText: formatDate(data.deadline),
    deadlineOverdue: isDeadlineOverdue(data.deadline, data.status),
    technicianId: Number(user?.id ?? 0),
  };

  return { isLoading, isError, data, error, taps, vm };
}
